import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CollectionsTriggersSettings, GraphQl, SmartStatementAgingExpression, TriggerRule, TriggerRuleForm } from '@anduin/api';
import { LaunchDarklyFlag, LaunchDarklyState } from '@anduin/auth';
import { CollectionsMode } from '@anduin/config';
import { getControlValueChanges } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';
import { StatementRecipientLabel } from '../../models';
import { CommonCollectionsState } from '../../state';
import { CollectionsSettingsStep, CollectionsTrackService, TriggerComparatorOperator } from '../../tracking';

const INVOICE_DUE_DATE_VARIABLE = 'invoice_due_date';
const INVOICE_DUE_DATE_DISPLAY = 'Invoice Due Date';

interface BaseTriggerStep {
  no_trigger_statement_enabled: boolean;
  whenNoTriggersApply: {
    no_trigger_statement_occurrence: string;
    no_trigger_statement_day: string;
  };
}

interface TriggerStepV1 extends BaseTriggerStep {
  steps: TriggerRuleForm[];
}

type TriggerStepStepV2 = Omit<TriggerRuleForm, 'priority'> & { ordinal: number; expressionId: number };
interface TriggerStepV2 extends BaseTriggerStep {
  // replace priority with ordinal
  steps: TriggerStepStepV2[];
}

type TriggerStep = TriggerStepV1 | TriggerStepV2;

@Component({
  selector: 'sequence-trigger-form',
  templateUrl: './sequence-trigger-form.component.html',
  styleUrls: ['./sequence-trigger-form.component.scss'],
  standalone: false,
})
export class SequenceTriggerFormComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<void>();

  // "valueMap.get('<comparator_value>')"
  triggerExpressionPattern = /valueMap.get\('(?<comparator>.*)'\)/;

  OCCURRENCE_OPTIONS = [
    { label: 'First', value: '1' },
    { label: 'Second', value: '2' },
    { label: 'Third', value: '3' },
    { label: 'Fourth', value: '4' },
    { label: 'Last', value: '-1' },
  ];

  DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Actual control that tracks the accepted triggers
  triggersControl = this.fb.array([]);

  // Helper control that tracks the 'in progress' triggers
  addTriggersControlArray = this.fb.array([]);

  noTriggerStatementEnabled: UntypedFormControl = this.fb.control(false);

  triggerForm: UntypedFormGroup = this.fb.group({
    steps: this.triggersControl,
    no_trigger_statement_enabled: this.noTriggerStatementEnabled,
    whenNoTriggersApply: this.fb.group({
      no_trigger_statement_occurrence: this.fb.control(this.OCCURRENCE_OPTIONS[0].value, {
        validators: Validators.required,
        updateOn: 'change',
      }),
      no_trigger_statement_day: this.fb.control(this.DAY_OPTIONS[1], { validators: Validators.required, updateOn: 'change' }),
    }),
  });

  get triggersControlArray(): UntypedFormArray {
    return this.triggerForm.controls['steps'] as UntypedFormArray;
  }

  get whenNoTriggersGroup(): UntypedFormGroup {
    return this.triggerForm.controls['whenNoTriggersApply'] as UntypedFormGroup;
  }

  @Input()
  set triggers(triggerRules: TriggerRule[] | SmartStatementAgingExpression[]) {
    this.triggersControlArray.clear();

    if (this.isCollectionsV2Enabled()) {
      (triggerRules as SmartStatementAgingExpression[])
        .map((statementExpression) => ({
          id: statementExpression.id,
          ordinal: statementExpression.ordinal,
          expressionId: statementExpression.expression.id,
          ...this.parseTriggerExpressionV2(statementExpression.expression.expressionValue),
        }))
        .sort((a, b) => a.ordinal - b.ordinal)
        .forEach((trigger) => this.triggersControlArray.push(this.fb.control(trigger)));
    } else {
      (triggerRules as TriggerRule[])
        .map(({ rule }, index) => ({
          id: index,
          priority: rule.priority,
          ...this.parseTriggerExpression(rule.expression),
        }))
        .forEach((trigger) => this.triggersControlArray.push(this.fb.control(trigger)));
    }

    this.updateTriggersPriority();
  }

  @Input()
  set noTriggersStatementEnabled(value: boolean) {
    this.triggerForm.controls['no_trigger_statement_enabled'].setValue(value);
    const action: keyof AbstractControl & ('enable' | 'disable') = value ? 'enable' : 'disable';
    this.whenNoTriggersGroup[action]();
  }

  @Input()
  set noTriggerStatementOccurrence(value: number) {
    this.whenNoTriggersGroup.controls['no_trigger_statement_occurrence'].setValue(value);
  }

  @Input()
  set noTriggerStatementDay(value: string) {
    this.whenNoTriggersGroup.controls['no_trigger_statement_day'].setValue(value);
  }

  private _disabled = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.triggerForm.disable();
    } else {
      this.triggerForm.enable();
      // Restore the no triggers group state based on checkbox
      if (!this.triggerForm.controls['no_trigger_statement_enabled'].value) {
        this.whenNoTriggersGroup.disable();
      }
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output()
  sequenceTriggerUpdate = new EventEmitter<
    { triggerRules: TriggerRule[] } & Pick<
      GraphQl.CadenceType,
      'no_trigger_statement_day' | 'no_trigger_statement_enabled' | 'no_trigger_statement_occurrence'
    >
  >();

  @Output()
  triggerUpdateV2 = new EventEmitter<CollectionsTriggersSettings>();

  @Output()
  isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  statementRecipientLabel$: Observable<StatementRecipientLabel> = this.store
    .select(CommonCollectionsState.getCollectionsMode)
    .pipe(map((mode) => (mode === CollectionsMode.CONTACT ? StatementRecipientLabel.CONTACT : StatementRecipientLabel.CLIENT)));

  constructor(
    private fb: UntypedFormBuilder,
    private readonly store: Store,
    private readonly trackService: CollectionsTrackService,
  ) {}

  ngOnInit(): void {
    getControlValueChanges(this.triggerForm.controls.no_trigger_statement_enabled)
      .pipe(
        tap((value) => {
          const action: keyof AbstractControl & ('enable' | 'disable') = value ? 'enable' : 'disable';
          this.whenNoTriggersGroup[action]();
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    (this.triggerForm.valueChanges as Observable<TriggerStep>)
      .pipe(
        debounceTime(100),
        tap((values) => {
          // Emit if the step form is valid
          this.isValid.emit(values.steps.every((step) => step.comparatorOperator && step.comparatorValue >= 0));

          if (this.isCollectionsV2Enabled()) {
            const sortedSteps: TriggerStepStepV2[] = [...(values as TriggerStepV2).steps].sort(
              (a, b) => SequenceTriggerFormComponent.getSortValue(a) - SequenceTriggerFormComponent.getSortValue(b),
            );

            this.triggerUpdateV2.emit({
              expressionList: (values as TriggerStepV2).steps.map((trigger) => {
                const display = `${INVOICE_DUE_DATE_DISPLAY} ${trigger.comparatorOperator} ${trigger.comparatorValue}`;
                const expressionValue = `${INVOICE_DUE_DATE_VARIABLE}${trigger.comparatorOperator}${trigger.comparatorValue}`;
                const ordinal = sortedSteps.findIndex((step) => step.id === trigger.id) + 1;

                return {
                  id: trigger.id,
                  active: true,
                  ordinal,
                  display,
                  expression: {
                    id: trigger.expressionId,
                    expressionValue,
                  },
                };
              }),
              noTriggerStatementEnabled: values.no_trigger_statement_enabled,
              noTriggerStatementDay: this.whenNoTriggersGroup.value.no_trigger_statement_day,
              noTriggerStatementOccurrence: +this.whenNoTriggersGroup.value.no_trigger_statement_occurrence,
            });

            return;
          }

          const whenNoTrigger = this.whenNoTriggersGroup.value;
          delete values.whenNoTriggersApply;

          const triggerRules = (values as TriggerStepV1).steps.map((trigger) => ({
            rule: {
              priority: trigger.priority,
              expression: this.formatTriggerToExpression(trigger.comparator, trigger.comparatorOperator, trigger.comparatorValue),
            },
          })) as TriggerRule[];

          this.sequenceTriggerUpdate.emit({
            triggerRules: triggerRules,
            no_trigger_statement_enabled: values.no_trigger_statement_enabled,
            no_trigger_statement_day: whenNoTrigger?.no_trigger_statement_day,
            no_trigger_statement_occurrence: whenNoTrigger?.no_trigger_statement_occurrence,
          });
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  addTrigger(trigger?: TriggerRuleForm): void {
    // if existing trigger, add to the accepted triggers
    if (trigger) {
      this.triggersControlArray.push(this.fb.control(trigger, Validators.required));

      // Otherwise add to our pending items
    } else {
      this.addTriggersControlArray.push(this.fb.control(null, Validators.required));
    }
  }

  // removing a trigger frmo our existing list
  removeTrigger(removeIndex: number) {
    const removed = this.triggersControlArray.at(removeIndex)?.value as TriggerRuleForm | undefined;
    this.triggersControlArray.removeAt(removeIndex);
    this.trackService.trackWorkflow(CollectionsSettingsStep.TRIGGER_REMOVED, {
      triggerCount: this.triggersControlArray.length,
      triggerOperator: SequenceTriggerFormComponent.toTriggerOperator(removed?.comparatorOperator),
      triggerValue: removed?.comparatorValue,
    });
  }

  // removing a trigger from our in progress list
  removeAddTrigger(): void {
    this.addTriggersControlArray.removeAt(0);
  }

  // Adding a brand new trigger to the step list
  updateNewTrigger(): void {
    const addedTrigger = this.addTriggersControlArray.at(0);
    const addedTriggerValue: TriggerRuleForm = addedTrigger.value;
    const currentTriggers: TriggerRuleForm[] = this.triggersControlArray.value.map((trigger) => ({ ...trigger, ...trigger.previousValue }));

    // adding the new step to a helper array
    currentTriggers.push(addedTriggerValue);
    // sorting the helper array
    currentTriggers.sort(this.triggerSort);

    // Now finding where we need to insert the new value
    const newPos = currentTriggers.findIndex((item) => item.id === addedTriggerValue.id);

    this.triggersControlArray.insert(newPos, addedTrigger);
    this.addTriggersControlArray.removeAt(0);
    this.updateTriggersPriority();
    this.trackService.trackWorkflow(CollectionsSettingsStep.TRIGGER_ADDED, {
      triggerCount: this.triggersControlArray.length,
      triggerOperator: SequenceTriggerFormComponent.toTriggerOperator(addedTriggerValue?.comparatorOperator),
      triggerValue: addedTriggerValue?.comparatorValue,
    });
  }

  updateExistingTrigger(prevPos: number): void {
    const updatedTrigger = this.triggersControlArray.at(prevPos);

    // ignore pending changes by overriding with last saved properties
    const currentTriggers: TriggerRuleForm[] = this.triggersControlArray.value.map((trigger) => ({ ...trigger, ...trigger.previousValue }));
    currentTriggers.sort(this.triggerSort);

    // Finding the position to put the updated trigger
    const newPos = currentTriggers.findIndex((item) => item.id === (updatedTrigger.value as TriggerRuleForm).id);

    // If new position and previous are the same, no need to move anything
    if (newPos !== prevPos) {
      const controls = this.triggersControlArray.controls;
      currentTriggers.forEach((trigger, index) => {
        const oldIndex = controls.findIndex(({ value }) => value.id === trigger.id);
        this.triggersControlArray.insert(index < oldIndex ? index : index + 1, controls[oldIndex]);
        this.triggersControlArray.removeAt(index < oldIndex ? oldIndex + 1 : oldIndex);
      });
      this.updateTriggersPriority();
    }
  }

  updateTriggersPriority(): void {
    const isCollectionsV2Enabled = this.isCollectionsV2Enabled();

    this.triggersControlArray.controls.forEach((control, index) => {
      control.patchValue(
        {
          ...control.value,
          ...(isCollectionsV2Enabled ? { ordinal: index + 1 } : { priority: this.triggersControlArray.length - index + 1 }),
        },
        { emitEvent: false },
      );
    });
  }

  /**
   * Sort by comparator, then by value
   */
  private triggerSort = (a: TriggerRuleForm, b: TriggerRuleForm) => {
    if (a.comparatorOperator !== b.comparatorOperator) {
      return a.comparatorOperator === '>' ? 1 : -1;
    }

    const difference = a.comparatorValue - b.comparatorValue;
    return a.comparatorOperator === '>' ? difference : difference * -1;
  };

  formatTriggerToExpression(comparator: string, operator: string, value: number): string {
    if (operator === '<=') {
      operator = '>=';
      value = value * -1;
    }

    if (value === 0) {
      operator = '>=';
    }

    return `valueMap.get('${comparator}') ${operator} ${value}`;
  }

  parseTriggerExpression(expression: string): Omit<TriggerRuleForm, 'id' | 'priority'> {
    const expressionSplit = expression.split(' ');
    let operator: string = expressionSplit[1];
    let value: number = +expressionSplit[2];
    const comparator = this.triggerExpressionPattern.exec(expressionSplit[0])?.groups['comparator'];

    if (value <= 0) {
      value = value * -1;
      operator = '<=';
    }

    return {
      comparator: comparator,
      comparatorOperator: operator,
      comparatorValue: value,
      previousValue: {
        comparatorOperator: operator,
        comparatorValue: value,
      },
    };
  }

  markAllAsTouched(): void {
    this.triggerForm.markAllAsTouched();
  }

  reset(
    triggersSettings: CollectionsTriggersSettings | (Omit<CollectionsTriggersSettings, 'expressionList'> & { triggers: TriggerRule[] }),
  ): void {
    this.triggers = 'expressionList' in triggersSettings ? triggersSettings.expressionList : triggersSettings.triggers;
    this.noTriggersStatementEnabled = triggersSettings.noTriggerStatementEnabled;
    this.noTriggerStatementOccurrence = triggersSettings.noTriggerStatementOccurrence;
    this.noTriggerStatementDay = triggersSettings.noTriggerStatementDay;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /** Collections v2 */

  isCollectionsV2Enabled(): boolean {
    return this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2));
  }

  parseTriggerExpressionV2(expression: string): Omit<TriggerRuleForm, 'id' | 'priority'> {
    const comparatorAndValue = expression.replace(INVOICE_DUE_DATE_VARIABLE, '');
    const valueIndex = comparatorAndValue.match(/[0-9]/).index;
    const operator = comparatorAndValue.slice(0, valueIndex);
    const value = +comparatorAndValue.slice(valueIndex);

    return {
      comparator: INVOICE_DUE_DATE_VARIABLE,
      comparatorOperator: operator,
      comparatorValue: value,
      previousValue: {
        comparatorOperator: operator,
        comparatorValue: value,
      },
    };
  }

  static getSortValue(trigger: TriggerStepStepV2): number {
    return trigger.comparatorValue / (trigger.comparatorOperator === '>' ? 1 : -1);
  }

  static toTriggerOperator(raw: string | undefined): TriggerComparatorOperator | undefined {
    return raw === '<' || raw === '<=' || raw === '>' || raw === '>=' || raw === '=' ? raw : undefined;
  }
}
